import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/helpers';
import { NetWorthChart } from '../components/NetWorthChart';
import { Trash2, ArrowLeft, Edit3, Check, X, TrendingUp, DollarSign, Home, Receipt, Building2, User } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useProperties } from '../hooks/useProperties';
import { Transaction } from '../types';
import { enhancedTransactionService } from '../services/database';

const mockYearlyData = [
  { year: 2024, rent: 30000, taxes: 6000, expenses: 8000, appreciation: 15000 },
  { year: 2025, rent: 31000, taxes: 6200, expenses: 8200, appreciation: 16000 },
  { year: 2026, rent: 32000, taxes: 6400, expenses: 8400, appreciation: 17000 },
];

export const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProperty, updateProperty } = useProperties();
  const property = getProperty(id!);

  // Editable income/expense rows
  const [rows, setRows] = useState(() =>
    property
      ? [
          { type: 'income', label: 'Rent', amount: property.monthly_rent },
          { type: 'expense', label: 'Taxes', amount: property.annual_taxes },
          { type: 'expense', label: 'Expenses', amount: property.annual_expenses },
        ]
      : []
  );
  const [editingRow, setEditingRow] = useState<{ idx: number; field: 'label' | 'amount'; value: string } | null>(null);
  const [adding, setAdding] = useState<'income' | 'expense' | null>(null);
  const [newRow, setNewRow] = useState({ label: '', amount: '' });
  const [selectedPeriod, setSelectedPeriod] = useState<'hour' | 'day' | 'month' | 'year'>('month');
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState<number | null>(null);

  // Editable property fields
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingPurchasePrice, setEditingPurchasePrice] = useState(false);
  const [addressValue, setAddressValue] = useState(property?.address || '');
  const [purchasePriceValue, setPurchasePriceValue] = useState(property?.purchase_price?.toString() || '');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveTimeout, setSaveTimeout] = React.useState<NodeJS.Timeout | null>(null);
  
  // Property transactions
  const [propertyTransactions, setPropertyTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Update values when property changes
  React.useEffect(() => {
    if (property) {
      setAddressValue(property.address);
      setPurchasePriceValue(property.purchase_price?.toString() || '');
    }
  }, [property]);

  // Load property transactions
  useEffect(() => {
    const loadPropertyTransactions = async () => {
      if (!property?.id) return;
      
      setLoadingTransactions(true);
      try {
        const transactions = await enhancedTransactionService.getTransactionsByProperty(property.id);
        setPropertyTransactions(transactions);
      } catch (error) {
        console.error('Error loading property transactions:', error);
      } finally {
        setLoadingTransactions(false);
      }
    };

    loadPropertyTransactions();
  }, [property?.id]);
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);
  
  const handleAddressSave = async () => {
    if (property && addressValue.trim()) {
      setSaveStatus('saving');
      try {
        await updateProperty(property.id, { address: addressValue.trim() });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }
    setEditingAddress(false);
  };

  const handlePurchasePriceSave = async () => {
    if (property && purchasePriceValue.trim()) {
      const price = parseFloat(purchasePriceValue);
      if (!isNaN(price) && price > 0) {
        setSaveStatus('saving');
        try {
          await updateProperty(property.id, { purchase_price: price });
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 3000);
        }
      }
    }
    setEditingPurchasePrice(false);
  };
  
  // Debounced auto-save for address changes
  const handleAddressChange = (value: string) => {
    setAddressValue(value);
    
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    const timeout = setTimeout(async () => {
      if (property && value.trim() && value.trim() !== property.address) {
        setSaveStatus('saving');
        try {
          await updateProperty(property.id, { address: value.trim() });
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 3000);
        }
      }
    }, 1000);
    
    setSaveTimeout(timeout);
  };
  
  const handleAddressKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAddressSave();
    if (e.key === 'Escape') { 
      setEditingAddress(false); 
      setAddressValue(property?.address || ''); 
    }
  };

  const handlePurchasePriceKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePurchasePriceSave();
    if (e.key === 'Escape') { 
      setEditingPurchasePrice(false); 
      setPurchasePriceValue(property?.purchase_price?.toString() || ''); 
    }
  };

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Property not found</h2>
          <p className="text-gray-600 mb-4">The property you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/app/properties')}
            className="bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  // Calculate metrics
  const appreciation = property.current_value - property.purchase_price;
  const appreciationPercent = (appreciation / property.purchase_price) * 100;
  const cashflow = rows.reduce((sum, row) =>
    row.type === 'income' ? sum + Number(row.amount) : sum - Number(row.amount), 0
  );

  // Mock chart data
  const chartData = [
    { date: '2023-12-31', value: property.purchase_price },
    { date: '2024-05-31', value: property.purchase_price + (appreciation * 0.6) },
    { date: '2024-12-30', value: property.current_value },
  ];
  const chartChange = chartData[chartData.length - 1].value - chartData[0].value;
  const chartChangePct = (chartChange / chartData[0].value) * 100;

  // Inline edit handlers for table rows
  const handleRowEdit = (idx: number, field: 'label' | 'amount', value: string) => setEditingRow({ idx, field, value });
  const handleRowEditChange = (value: string) => setEditingRow((prev) => prev ? { ...prev, value } : prev);
  const handleRowEditSave = () => {
    if (!editingRow) return;
    setRows((prev) => prev.map((row, i) =>
      i === editingRow.idx ? { ...row, [editingRow.field]: editingRow.field === 'amount' ? Number(editingRow.value) : editingRow.value } : row
    ));
    setEditingRow(null);
  };
  const handleRowEditKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRowEditSave();
    if (e.key === 'Escape') setEditingRow(null);
  };

  // Add new income/expense row
  const handleAddRow = (type: 'income' | 'expense') => {
    setAdding(type);
    setNewRow({ label: '', amount: '' });
  };
  const handleAddRowSave = () => {
    if (!newRow.label || !newRow.amount) return;
    setRows((prev) => [...prev, { type: adding!, label: newRow.label, amount: Number(newRow.amount) }]);
    setAdding(null);
    setNewRow({ label: '', amount: '' });
  };

  // Row deletion with confirmation
  const handleDeleteRow = (idx: number) => {
    setDeleteConfirmIdx(idx);
  };
  const confirmDeleteRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setDeleteConfirmIdx(null);
  };
  const cancelDeleteRow = () => setDeleteConfirmIdx(null);

  // Drag-and-drop reordering
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(rows);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setRows(reordered);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <button 
              onClick={() => navigate('/app/properties')} 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Properties</span>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Property Address */}
        <div className="flex items-center gap-3">
          {editingAddress ? (
                <div className="flex items-center gap-2 flex-1">
            <input
              type="text"
                    className="text-2xl md:text-3xl font-bold text-gray-900 border-2 border-brand-500 rounded-lg px-3 py-2 flex-1 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              value={addressValue}
              autoFocus
              onChange={e => handleAddressChange(e.target.value)}
              onBlur={handleAddressSave}
              onKeyDown={handleAddressKey}
            />
                  <button
                    onClick={handleAddressSave}
                    className="p-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingAddress(false);
                      setAddressValue(property.address);
                    }}
                    className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex-1">
              {addressValue}
            </h1>
                  <button
                    onClick={() => setEditingAddress(true)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
          )}
            </div>
          
          {/* Save Status Indicator */}
          {saveStatus === 'saving' && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Saving...</span>
            </div>
          )}
          {saveStatus === 'saved' && (
            <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="w-4 h-4" />
              <span>Saved!</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
                <X className="w-4 h-4" />
              <span>Error saving</span>
            </div>
          )}

            {/* Property Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Purchase Price */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Purchase Price</label>
                  {editingPurchasePrice ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={handlePurchasePriceSave}
                        className="p-1 bg-brand-500 text-white rounded hover:bg-brand-600 transition-colors"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingPurchasePrice(false);
                          setPurchasePriceValue(property.purchase_price?.toString() || '');
                        }}
                        className="p-1 bg-gray-300 text-gray-600 rounded hover:bg-gray-400 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingPurchasePrice(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {editingPurchasePrice ? (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border-2 border-brand-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-lg font-semibold"
                      value={purchasePriceValue}
                      onChange={(e) => setPurchasePriceValue(e.target.value)}
                      onKeyDown={handlePurchasePriceKey}
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(property.purchase_price)}
                  </div>
                )}
              </div>

              {/* Current Value */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Current Value</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(property.current_value)}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">
                    +{formatCurrency(appreciation)} ({appreciationPercent.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
      </div>

        {/* Income & Expenses Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Income & Expenses</h2>
          <div className="flex gap-2">
            <button
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 text-sm font-medium transition-colors"
              onClick={() => handleAddRow('income')}
            >
                <DollarSign className="w-4 h-4" />
                Add Income
            </button>
            <button
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 text-sm font-medium transition-colors"
              onClick={() => handleAddRow('expense')}
            >
                <DollarSign className="w-4 h-4" />
                Add Expense
            </button>
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="rows-droppable">
            {(provided) => (
                <div className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                  {rows.map((row, idx) => (
                    <Draggable key={idx} draggableId={String(idx)} index={idx}>
                      {(draggableProvided, snapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={`bg-gray-50 rounded-lg p-4 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              <div {...draggableProvided.dragHandleProps} className="cursor-move text-gray-400">
                                ⋮⋮
                              </div>
                              <div className="flex-1">
                            {editingRow?.idx === idx && editingRow.field === 'label' ? (
                              <input
                                type="text"
                                    className="w-full border-2 border-brand-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                value={editingRow.value}
                                autoFocus
                                onChange={e => handleRowEditChange(e.target.value)}
                                onBlur={handleRowEditSave}
                                onKeyDown={handleRowEditKey}
                              />
                            ) : (
                                  <div
                                    className="font-medium text-gray-900 cursor-pointer hover:text-brand-600 transition-colors"
                                onClick={() => handleRowEdit(idx, 'label', row.label)}
                              >
                                {row.label}
                                  </div>
                            )}
                                <div className="text-xs text-gray-500 capitalize">{row.type}</div>
                              </div>
                              <div className="text-right">
                            {editingRow?.idx === idx && editingRow.field === 'amount' ? (
                              <input
                                type="number"
                                    className="w-24 border-2 border-brand-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                value={editingRow.value}
                                autoFocus
                                onChange={e => handleRowEditChange(e.target.value)}
                                onBlur={handleRowEditSave}
                                onKeyDown={handleRowEditKey}
                              />
                            ) : (
                                  <div
                                    className={`font-semibold cursor-pointer hover:text-brand-600 transition-colors ${
                                      row.type === 'income' ? 'text-green-600' : 'text-red-600'
                                    }`}
                                onClick={() => handleRowEdit(idx, 'amount', String(row.amount))}
                              >
                                {formatCurrency(row.amount)}
                                  </div>
                            )}
                              </div>
                            </div>
                            <div className="ml-4">
                            {deleteConfirmIdx === idx ? (
                                <div className="flex items-center gap-2">
                                  <button 
                                    className="text-xs text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 border border-red-200" 
                                    onClick={() => confirmDeleteRow(idx)}
                                  >
                                    Delete
                                  </button>
                                  <button 
                                    className="text-xs text-gray-500 px-2 py-1 rounded hover:bg-gray-100 border border-gray-200" 
                                    onClick={cancelDeleteRow}
                                  >
                                    Cancel
                                  </button>
                                </div>
                            ) : (
                              <button
                                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-600 transition-colors"
                                onClick={() => handleDeleteRow(idx)}
                                aria-label="Delete row"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  
                  {adding && (
                    <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                      <div className="flex items-center gap-4">
                        <div className="text-gray-400">⋮⋮</div>
                        <div className="flex-1">
                        <input
                          type="text"
                            className="w-full border-2 border-brand-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                          value={newRow.label}
                          autoFocus
                            placeholder="Category name"
                          onChange={e => setNewRow((prev) => ({ ...prev, label: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') handleAddRowSave(); if (e.key === 'Escape') setAdding(null); }}
                        />
                        </div>
                        <div className="text-right">
                        <input
                          type="number"
                            className="w-24 border-2 border-brand-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                          value={newRow.amount}
                            placeholder="0.00"
                          onChange={e => setNewRow((prev) => ({ ...prev, amount: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddRowSave(); if (e.key === 'Escape') setAdding(null); }}
                        />
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            className="text-xs text-brand-600 font-medium px-2 py-1 rounded hover:bg-brand-50 border border-brand-200" 
                            onClick={handleAddRowSave}
                          >
                            Save
                          </button>
                          <button 
                            className="text-xs text-gray-500 px-2 py-1 rounded hover:bg-gray-100 border border-gray-200" 
                            onClick={() => setAdding(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
            )}
          </Droppable>
        </DragDropContext>
          
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Monthly Cashflow</span>
              <span className={`text-xl font-bold ${cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(cashflow)}
              </span>
            </div>
          </div>
        </div>

        {/* Property Transactions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Property Transactions</h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Building2 className="w-4 h-4" />
              <span>{propertyTransactions.length} transactions</span>
            </div>
          </div>

          {loadingTransactions ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading transactions...</p>
            </div>
          ) : propertyTransactions.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
              <p className="text-gray-600 mb-4">
                Upload a document or add transactions to see them here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {propertyTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {transaction.type === 'income' ? (
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        {transaction.classification === 'business' ? (
                          <Building2 className="w-4 h-4 text-blue-600" />
                        ) : (
                          <User className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{transaction.category}</span>
                        <span>{new Date(transaction.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(transaction.amount))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

        {/* Property Value Trend */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <h2 className="text-xl font-bold text-gray-900">Property Value Trend</h2>
            <div className="flex gap-1">
            {['hour', 'day', 'month', 'year'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period as any)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    selectedPeriod === period 
                      ? 'bg-brand-500 text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <NetWorthChart data={chartData} period={selectedPeriod as any} />
          <div className="flex justify-end mt-4">
            <span className="text-sm font-medium text-green-600">
              {chartChange >= 0 ? '+' : ''}{formatCurrency(chartChange)} ({chartChangePct >= 0 ? '+' : ''}{chartChangePct.toFixed(1)}%) this month
            </span>
        </div>
      </div>

      {/* Yearly Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Yearly Breakdown</h2>
          <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-3 font-medium">Year</th>
                  <th className="py-3 font-medium">Rent</th>
                  <th className="py-3 font-medium">Taxes</th>
                  <th className="py-3 font-medium">Expenses</th>
                  <th className="py-3 font-medium">Appreciation</th>
            </tr>
          </thead>
          <tbody>
            {mockYearlyData.map((row) => (
                  <tr key={row.year} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-3 font-medium text-gray-900">{row.year}</td>
                    <td className="py-3 text-green-600 font-medium">{formatCurrency(row.rent)}</td>
                    <td className="py-3 text-red-600 font-medium">{formatCurrency(row.taxes)}</td>
                    <td className="py-3 text-red-600 font-medium">{formatCurrency(row.expenses)}</td>
                    <td className="py-3 text-green-600 font-medium">{formatCurrency(row.appreciation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      </div>
    </div>
  );
}; 